import { getJson, postForFile, postJson, FileResult } from './files';

type Row = Record<string, unknown>;

// ── E-commerce and quick commerce (ERP vs marketplace settlement) ────────────

export type MarketplaceEndpoint = 'ecommerce-recon' | 'quickcomm-recon';

export interface MarketplaceOptions {
  platform: string;
  match_on: string;
  tolerance: number;
  settlement_cycle_days: number;
  stock_holding_days: number;
  strip_leading_zeros: boolean;
  as_of: string;
}

export interface MarketplaceInputs {
  erp: File;
  platform: File;
  /** Optional extras keyed by form field (vendor_file, stock_file, erp_stock_file). */
  optional: Record<string, File | undefined>;
}

export interface MarketplaceSummary {
  match_on: string;
  formats?: { erp?: string; platform?: string };
  headline: Record<string, number>;
  summary_rows: { Metric: string; Value: unknown }[];
  matched_preview: Row[];
  unmatched_preview: Row[];
  cancelled_preview: Row[];
  exchanges: Row[];
  stock_reco?: Row[];
  warehouse_stock?: Row[];
  stock_aging?: Row[];
}

function marketplaceForm({ erp, platform, optional }: MarketplaceInputs, options: MarketplaceOptions) {
  const form = new FormData();
  form.append('erp_file', erp);
  form.append('platform_file', platform);
  Object.entries(optional).forEach(([field, file]) => file && form.append(field, file));
  form.append('options', JSON.stringify(options));
  return form;
}

export function reconcileMarketplace(endpoint: MarketplaceEndpoint, inputs: MarketplaceInputs, options: MarketplaceOptions) {
  return postJson<MarketplaceSummary>(`/${endpoint}/summary`, marketplaceForm(inputs, options), 'Reconciliation failed');
}

export function downloadMarketplaceReport(
  endpoint: MarketplaceEndpoint,
  inputs: MarketplaceInputs,
  options: MarketplaceOptions
): Promise<FileResult<null>> {
  return postForFile<null>(`/${endpoint}/report`, marketplaceForm(inputs, options), 'Reconciliation.xlsx', 'Failed to build the report');
}

// ── Payment gateways ─────────────────────────────────────────────────────────

export interface GatewayInputs {
  erp: File;
  bank?: File;
  /** One settlement file per gateway. */
  settlements: { gateway: string; file: File }[];
  as_of: string;
}

export interface GatewaySummary {
  meta: { generated_at: string; as_of: string; gateways: string[] };
  per_source: Row[];
  totals: Record<string, unknown>;
  unmapped: { source: string; rows: number; amount: string }[];
  headline: Record<string, number>;
  gateway_matched_preview: Row[];
  gateway_unmatched_preview: Row[];
  bank_received_preview: Row[];
  bank_not_received_preview: Row[];
  cod_reconciliation_preview: Row[];
  exceptions_preview: Row[];
}

function gatewayForm({ erp, bank, settlements, as_of }: GatewayInputs) {
  const form = new FormData();
  form.append('erp_file', erp);
  if (bank) form.append('bank_file', bank);
  settlements.forEach((s) => form.append('settlement_files', s.file));
  form.append('options', JSON.stringify({ as_of, sources: settlements.map((s) => s.gateway) }));
  return form;
}

export async function fetchGateways() {
  const { gateways } = await getJson<{ gateways: string[] }>('/paygw-recon/gateways', 'Could not load the gateway list');
  return gateways;
}

export function reconcileGateways(inputs: GatewayInputs) {
  return postJson<GatewaySummary>('/paygw-recon/summary', gatewayForm(inputs), 'Reconciliation failed');
}

export function downloadGatewayReport(inputs: GatewayInputs): Promise<FileResult<null>> {
  return postForFile<null>('/paygw-recon/report', gatewayForm(inputs), 'Channel_Reconciliation.xlsx', 'Failed to build the report');
}

// ── Property / OTA ───────────────────────────────────────────────────────────

export interface PropertySummary {
  detected: Record<string, string | null>;
  labels: Record<string, string>;
  slot_order: string[];
  unrecognized: string[];
  has_stayledger: boolean;
  has_any_ota: boolean;
  can_run: boolean;
  rows: Row[];
  summary: { total: number; matched: number; pending: number; attention: number };
  by_result: { result: string; count: number }[];
  by_platform: { platform: string; total: number; matched: number; pending: number; issues: number }[];
}

function propertyForm(files: File[]) {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  return form;
}

export function reconcileProperty(files: File[]) {
  return postJson<PropertySummary>('/property-recon/summary', propertyForm(files), 'Reconciliation failed');
}

export function downloadPropertyReport(files: File[]): Promise<FileResult<null>> {
  return postForFile<null>('/property-recon/report', propertyForm(files), 'Reco_Desk_Reconciliation.xlsx', 'Failed to build the report');
}
